/**
 * 웨딩홀 이벤트 API
 * wbapp-webview 공통 패턴: BASE_URL(wbapp-api) 직접 호출 (weddingHall.ts, booking.ts 등과 동일)
 */
import axios from "@src/api/axios";
import { BASE_URL } from "@src/utils";
import type { ContentComponent } from "@src/types/weddinghallEvent";

/** GET /api/events 리스트 한 건 */
export interface EventListCardItem {
  weddingHallEventId: number;
  weddinghallPartnerUuid: string;
  eventTitle: string;
  eventDesc?: string;
  eventStartTsp: string;
  eventEndTsp: string;
  eventOrd: number;
  eventBadgeCd: string | null;
  eventTagCds: string[];
  regTsp?: string;
}

/** GET /api/events 응답 (item + total) */
export interface EventListResponse {
  item: EventListCardItem[];
  total: number;
}

/** GET /api/event-tags 한 건 */
export interface EventTagOption {
  eventTagCd: string;
  eventTagName: string;
  eventTagAlias?: string | null;
  tagOrd: number;
}

/** GET /api/event-badges 한 건 */
export interface EventBadgeOption {
  eventBadgeCd: string;
  eventBadgeName: string;
  badgeOrd: number;
}

export interface GetEventListParams {
  regions?: string[];
  eventTagCds?: string[];
  sort?: "rank" | "latest";
  page?: number;
  size?: number;
}

/** 웨딩홀 이벤트 리스트 */
export const getEventList = async (
  params: GetEventListParams = {},
): Promise<EventListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.regions?.length) {
    params.regions.forEach((r) => searchParams.append("regions", r));
  }
  if (params.eventTagCds?.length) {
    params.eventTagCds.forEach((c) => searchParams.append("eventTagCds", c));
  }
  if (params.sort) searchParams.set("sort", params.sort);
  if (params.page != null) searchParams.set("page", String(params.page));
  if (params.size != null) searchParams.set("size", String(params.size));

  const qs = searchParams.toString();
  const res = await axios.get<EventListResponse>(
    `${BASE_URL}/api/events${qs ? `?${qs}` : ""}`,
  );
  const data = res.data;
  return { item: data?.item ?? [], total: data?.total ?? 0 };
};

/** 이벤트 태그 옵션 (필터·카드 태그명) */
export const getEventTags = async (): Promise<EventTagOption[]> => {
  const res = await axios.get<{ item: EventTagOption[]; total: number }>(
    `${BASE_URL}/api/event-tags`,
  );
  return res.data?.item ?? [];
};

/** 배지 옵션 */
export const getEventBadges = async (): Promise<EventBadgeOption[]> => {
  const res = await axios.get<{
    item: EventBadgeOption[];
    total: number;
  }>(`${BASE_URL}/api/event-badges`);
  return res.data?.item ?? [];
};

/** GET /api/wedding-halls/{uuid}/active-events 응답 한 건 */
export interface ActiveEventByHallItem {
  weddingHallEventId: number;
  eventTitle: string;
  eventDesc?: string | null;
  eventStartTsp: string;
  eventEndTsp: string;
  eventOrd: number;
  eventBadgeCd: string | null;
}

/** 웨딩홀 UUID 목록 기준 활성 이벤트 태그 배치 조회 (리스트 카드 [이벤트] 뱃지용) */
export const getActiveEventTagsByPartnerUuids = async (
  partnerUuids: string[],
): Promise<Record<string, string[]>> => {
  if (!partnerUuids?.length) return {};
  const searchParams = new URLSearchParams();
  partnerUuids.forEach((uuid) => searchParams.append("partnerUuids", uuid));
  const res = await axios.get<Record<string, string[]>>(
    `${BASE_URL}/api/wedding-halls/active-event-tags?${searchParams.toString()}`,
  );
  return res.data ?? {};
};

/** 웨딩홀별 활성 이벤트 조회 (혜택 섹션용) */
export const getActiveEventsByHall = async (
  weddingHallPartnerUuid: string,
): Promise<ActiveEventByHallItem[]> => {
  const res = await axios.get<{ item: ActiveEventByHallItem[]; total: number }>(
    `${BASE_URL}/api/wedding-halls/${weddingHallPartnerUuid}/active-events`,
  );
  return res.data?.item ?? [];
};

// --- 이벤트 상세 (GET /api/events/{id}) ---

export interface EventDetailComponentDto {
  componentTypeCd: string;
  componentOrd: number;
  textStyleCd?: string | null;
  textTitle?: string | null;
  textBody?: string | null;
  bulletStyleCd?: string | null;
  bulletTitle?: string | null;
  bulletItems?: string[] | null;
  tableTitle?: string | null;
  tableDesc?: string | null;
  tableRows?: { tableRowLabel: string; tableRowValue: string; tableRowOrd: number; tableRowHighlightYn?: boolean | null }[] | null;
  tableItems?: { tableItemName: string; tableItemOrd: number; rows: { tableRowLabel: string; tableRowValue: string; tableRowOrd: number; tableRowHighlightYn?: boolean | null }[] }[] | null;
  imageUrl?: string | null;
  imageStyleCd?: string | null;
  imageOrd?: number | null;
}

export interface EventDetailDto {
  weddingHallEventId: number;
  weddinghallPartnerUuid: string;
  eventTitle: string;
  eventDesc: string | null;
  eventStartTsp: string;
  eventEndTsp: string;
  eventStatus: string | null;
  eventOrd: number;
  eventBadgeCd: string | null;
  eventTagCds: string[];
  components: EventDetailComponentDto[];
}

interface SingleResponse<T> {
  item: T;
  result?: { code?: number; message?: string };
}

/** 이벤트 상세 조회 */
export const getEventDetail = async (
  weddingHallEventId: number | string,
): Promise<EventDetailDto | null> => {
  const id = String(weddingHallEventId);
  const res = await axios.get<SingleResponse<EventDetailDto>>(
    `${BASE_URL}/api/events/${id}`,
  );
  const item = res.data?.item;
  if (!item) return null;
  return item;
};

/** API 컴포넌트 → EventContentRenderer용 ContentComponent[] 변환 */
export function mapEventDetailComponentsToContentComponents(
  components: EventDetailComponentDto[] | null | undefined,
): ContentComponent[] {
  if (!components?.length) return [];
  const styleMap: Record<string, "기본" | "박스" | "여백있음" | "여백없음"> = {
    DEFAULT: "기본",
    BOX: "박스",
    MARGIN: "여백있음",
    WITH_MARGIN: "여백있음",
    NOMARGIN: "여백없음",
    NO_MARGIN: "여백없음",
  };
  return components
    .sort((a, b) => (a.componentOrd ?? 0) - (b.componentOrd ?? 0))
    .map((c, index) => {
      const typeCd = (c.componentTypeCd || "").toUpperCase();
      const order = c.componentOrd ?? index;
      const id = `evt-${index}-${order}`;
      const rawStyle =
        typeCd === "IMAGE" ? c.imageStyleCd : c.textStyleCd || c.bulletStyleCd;
      const styleCd = (rawStyle || "DEFAULT").toUpperCase().replace(/[- ]/g, "_");
      const style = styleMap[styleCd] ?? "기본";

      if (typeCd === "TEXT") {
        return {
          id,
          type: "text" as const,
          style,
          title: c.textTitle ?? undefined,
          content: JSON.stringify({ text: c.textBody ?? "" }),
          order,
        };
      }
      if (typeCd === "BULLET") {
        return {
          id,
          type: "bullet" as const,
          style,
          title: c.bulletTitle ?? undefined,
          content: JSON.stringify({ items: c.bulletItems ?? [] }),
          order,
        };
      }
      if (typeCd === "TABLE") {
        const items = (c.tableItems ?? []).map((it) => ({
          name: it.tableItemName,
          rows: (it.rows ?? []).map((r) => ({
            content: r.tableRowLabel,
            amount: r.tableRowValue,
            highlight: r.tableRowHighlightYn ?? false,
          })),
        }));
        return {
          id,
          type: "table" as const,
          style,
          title: c.tableTitle ?? undefined,
          description: c.tableDesc ?? undefined,
          content: JSON.stringify({ items }),
          order,
        };
      }
      if (typeCd === "IMAGE") {
        return {
          id,
          type: "image" as const,
          style,
          content: JSON.stringify({
            imageUrl: c.imageUrl ?? "",
            linkUrl: undefined,
          }),
          order,
        };
      }
      if (typeCd === "DIVIDER") {
        return {
          id,
          type: "divider" as const,
          style: "기본",
          content: "{}",
          order,
        };
      }
      return {
        id,
        type: "text" as const,
        style: "기본",
        content: "{}",
        order,
      };
    });
}
